/**
 * AISettingsModal - Configure AI content generation settings
 *
 * Allows creators to specify CEFR level, content type, topic,
 * scenario, word count target, and custom requirements for AI-generated content.
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ModalShell from './ModalShell';

const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const CONTENT_TYPES = ['dialogue', 'article', 'news', 'speech', 'story'];
const WORD_COUNT_MIN = 50;
const WORD_COUNT_MAX = 1000;
const WORD_COUNT_DEFAULT = 200;

interface AISettingsModalProps {
  defaultLanguage?: string;
  onSubmit: (settings: {
    cefrLevel: string;
    contentType: string;
    topic: string;
    scenario?: string;
    targetWordCount: number;
    customRequirements?: string;
  }) => void;
  onBack: () => void;
  onClose: () => void;
}

const AISettingsModal: React.FC<AISettingsModalProps> = ({
  onSubmit,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation(['creator', 'common']);

  // Form state
  const [cefrLevel, setCefrLevel] = useState('B1');
  const [contentType, setContentType] = useState('dialogue');
  const [topic, setTopic] = useState('');
  const [scenario, setScenario] = useState('');
  const [targetWordCount, setTargetWordCount] = useState(WORD_COUNT_DEFAULT);
  const [customRequirements, setCustomRequirements] = useState('');

  const isValid = topic.trim().length > 0;

  const handleSubmit = useCallback(() => {
    if (!isValid) return;

    onSubmit({
      cefrLevel,
      contentType,
      topic: topic.trim(),
      scenario: scenario.trim() || undefined,
      targetWordCount,
      customRequirements: customRequirements.trim() || undefined,
    });
  }, [cefrLevel, contentType, topic, scenario, targetWordCount, customRequirements, isValid, onSubmit]);

  const handleWordCountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      setTargetWordCount(Math.min(WORD_COUNT_MAX, Math.max(WORD_COUNT_MIN, value)));
    }
  }, []);

  return (
    <ModalShell
      title={t('creator:workflow.resourceModal.aiSettingsTitle')}
      onClose={onClose}
      onBack={onBack}
      onNext={handleSubmit}
      nextDisabled={!isValid}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {/* CEFR Level */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label
            htmlFor="ai-cefr-level"
            style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-primary, #1a1a2e)' }}
          >
            {t('creator:workflow.resourceModal.cefrLevel')}
          </label>
          <select
            id="ai-cefr-level"
            value={cefrLevel}
            onChange={(e) => setCefrLevel(e.target.value)}
            style={{
              padding: '0.625rem 0.75rem',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: '8px',
              fontSize: '0.95rem',
              background: 'var(--bg-primary, #fff)',
              color: 'var(--text-primary, #1a1a2e)',
            }}
          >
            {CEFR_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>

        {/* Content Type */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label
            htmlFor="ai-content-type"
            style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-primary, #1a1a2e)' }}
          >
            {t('creator:workflow.resourceModal.contentType')}
          </label>
          <select
            id="ai-content-type"
            value={contentType}
            onChange={(e) => setContentType(e.target.value)}
            style={{
              padding: '0.625rem 0.75rem',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: '8px',
              fontSize: '0.95rem',
              background: 'var(--bg-primary, #fff)',
              color: 'var(--text-primary, #1a1a2e)',
            }}
          >
            {CONTENT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`creator:workflow.resourceModal.contentType_${type}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Topic (required) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label
            htmlFor="ai-topic"
            style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-primary, #1a1a2e)' }}
          >
            {t('creator:workflow.resourceModal.topic')}
            <span style={{ color: 'var(--error-color, #ef4444)', marginLeft: '4px' }}>*</span>
          </label>
          <input
            id="ai-topic"
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={t('creator:workflow.resourceModal.topicPlaceholder')}
            style={{
              padding: '0.625rem 0.75rem',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: '8px',
              fontSize: '0.95rem',
              background: 'var(--bg-primary, #fff)',
              color: 'var(--text-primary, #1a1a2e)',
            }}
          />
        </div>

        {/* Scenario (optional) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label
            htmlFor="ai-scenario"
            style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-primary, #1a1a2e)' }}
          >
            {t('creator:workflow.resourceModal.scenario')}
          </label>
          <input
            id="ai-scenario"
            type="text"
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
            placeholder={t('creator:workflow.resourceModal.scenarioPlaceholder')}
            style={{
              padding: '0.625rem 0.75rem',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: '8px',
              fontSize: '0.95rem',
              background: 'var(--bg-primary, #fff)',
              color: 'var(--text-primary, #1a1a2e)',
            }}
          />
        </div>

        {/* Target Word Count */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label
            htmlFor="ai-word-count"
            style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-primary, #1a1a2e)' }}
          >
            {t('creator:workflow.resourceModal.wordCountTarget')}
          </label>
          <input
            id="ai-word-count"
            type="number"
            min={WORD_COUNT_MIN}
            max={WORD_COUNT_MAX}
            value={targetWordCount}
            onChange={handleWordCountChange}
            style={{
              padding: '0.625rem 0.75rem',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: '8px',
              fontSize: '0.95rem',
              background: 'var(--bg-primary, #fff)',
              color: 'var(--text-primary, #1a1a2e)',
              width: '140px',
            }}
          />
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted, #9ca3af)' }}>
            {t('creator:workflow.resourceModal.wordCountRange', { min: WORD_COUNT_MIN, max: WORD_COUNT_MAX })}
          </span>
        </div>

        {/* Custom Requirements (optional) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
          <label
            htmlFor="ai-custom-requirements"
            style={{ fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-primary, #1a1a2e)' }}
          >
            {t('creator:workflow.resourceModal.customRequirements')}
          </label>
          <textarea
            id="ai-custom-requirements"
            value={customRequirements}
            onChange={(e) => setCustomRequirements(e.target.value)}
            rows={3}
            style={{
              padding: '0.625rem 0.75rem',
              border: '1px solid var(--border-color, #d1d5db)',
              borderRadius: '8px',
              fontSize: '0.95rem',
              background: 'var(--bg-primary, #fff)',
              color: 'var(--text-primary, #1a1a2e)',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        </div>
      </div>
    </ModalShell>
  );
};

export default AISettingsModal;
